/**
 * PGD 2b: Valaciclovir for shingles (herpes zoster)
 * Version: 1.0
 * Effective: 2024-01-31
 * Expiry: 2027-01-30
 *
 * QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
 */

import type { PGD } from "../types";

export const pgdValaciclovirShingles: PGD = {
  id: "pgd-2b-valaciclovir-shingles",
  reference: "2b",
  version: "1.0",
  drugRef: "valaciclovir",
  indication:
    "Treatment of acute herpes zoster (shingles) in eligible adults — preferred where adherence concerns or non-severe immunosuppression",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",

  population: {
    ageRange: { min: 18, max: 999 },
    pregnancyAllowed: false,
    breastfeedingAllowed: true,
    notes:
      "Breastfeeding allowed with caution. Use with caution in CKD — see exclusions for eGFR thresholds.",
  },

  duration: { days: 7 },

  doseBands: [
    {
      id: "valaciclovir-adult",
      resolveBy: "single",
      dose: 1000,
      frequency: "THREE times a day",
      formulations: [
        {
          formulationId: "valaciclovir-500-tab",
          instruction: "Take TWO 500mg tablets THREE times a day for 7 days",
          quantity: { count: 42, label: "42 tablets" },
        },
      ],
    },
  ],

  preSupplyChecks: [
    {
      id: "ex-allergy",
      question:
        "Is the patient allergic to valaciclovir, aciclovir or any excipient?",
    },
    {
      id: "ex-long-term-valaciclovir",
      question: "Is the patient already on a long term course of valaciclovir or aciclovir?",
    },
    {
      id: "ex-pregnancy",
      question: "Is the patient pregnant?",
    },
    {
      id: "ex-severely-immuno",
      question: "Is the patient severely immunosuppressed?",
    },
    {
      id: "ex-ckd-60",
      question: "Does the patient have CKD with eGFR < 60 ml/min/1.73m²?",
    },
    {
      id: "ex-dress",
      question:
        "Has the patient previously had DRESS / Stevens-Johnson syndrome / TEN with antivirals?",
    },
    {
      id: "ex-dehydration",
      question:
        "Is the patient unable to maintain adequate hydration during treatment?",
    },
    {
      id: "ex-concurrent-nephrotoxic",
      question:
        "Is the patient taking concurrent nephrotoxic drugs (e.g. ciclosporin, tacrolimus)?",
    },
    {
      id: "ex-mycophenolate",
      question: "Is the patient currently taking mycophenolate mofetil?",
    },
    {
      id: "ex-zidovudine",
      question: "Is the patient currently taking zidovudine?",
    },
    {
      id: "ex-antiviral-recent",
      question:
        "Has the patient taken systemic antiviral therapy for shingles in the past 7 days?",
    },
  ],

  cautions: [
    {
      id: "caution-hydration",
      text: "Maintain good hydration throughout treatment to reduce risk of renal adverse effects",
      adviceText: "Encourage at least 1.5–2 L of fluid daily",
    },
    {
      id: "caution-renal",
      text: "Use with caution in mild–moderate CKD; dose reduction may be needed in eGFR 30–60",
    },
    {
      id: "caution-elderly",
      text: "Elderly patients are at greater risk of CNS adverse effects — monitor for confusion or drowsiness",
    },
    {
      id: "caution-breastfeeding",
      text: "Breastfeeding — valaciclovir metabolite is excreted in breast milk; consider on individual basis",
    },
  ],

  counselling: [
    "Take TWO 500mg tablets THREE times a day for 7 days",
    "Complete the full 7-day course even if the rash starts to improve",
    "Maintain good hydration — at least 1.5–2 L of fluid per day",
    "Report any new confusion, drowsiness, hallucinations or reduced urine output immediately",
    "Pain may persist after the rash heals (post-herpetic neuralgia) — review with GP if persistent",
    "Avoid contact with pregnant women and the immunosuppressed until lesions have crusted over",
    "Cover lesions where practical to reduce transmission risk",
    "Return for review if symptoms worsen, new neurological symptoms develop, or the rash spreads",
  ],
};
